<template>
  <div class="class-selector">
    <el-card class="selector-card">
      <div slot="header" class="selector-header">
        <h2>{{ title }}</h2>
        <div class="welcome-message" v-if="teacherInfo">
          您好！{{ teacherInfo.nickname }}
        </div>
      </div>
      
      <div v-loading="loading" element-loading-text="加载中..." style="min-height: 300px;">
        <div v-if="!loading && boundClasses.length === 0" class="empty-state">
          <i class="el-icon-office-building empty-icon"></i>
          <p style="margin: 0;">您还没有加入任何班级</p>
          <p>如果班里已有老师创建了班级，请加入班级，否则请创建班级</p>
          <div class="action-buttons">
            <el-button 
              type="primary" 
              @click="goToJoinClass"
              :size="buttonSize">
              加入班级
            </el-button>
            <el-button 
              type="success" 
              @click="goToCreateClass"
              :size="buttonSize">
              创建班级
            </el-button>
          </div>
        </div>
        
        <div v-else-if="!loading" class="class-list">
          <div 
            v-for="classItem in groupedClasses" 
            :key="classItem.classId"
            class="class-item"
            :class="getClassItemClass(classItem)"
            @click="selectClass(classItem)"
          >
            <div class="class-header">
              <div class="class-name">{{ classItem.className }}</div>
              <div class="class-actions">
                <el-button 
                  type="primary" 
                  size="mini"
                  icon="el-icon-right"
                  @click.stop="selectClass(classItem)">
                  进入班级
                </el-button>
              </div>
            </div>
            <div class="class-main-info">
              <div class="class-details">
                <span class="class-current-subject">任教科目: {{ classItem.subjectName || getSubjectName(classItem.subjectId) }}</span>
                <span class="class-divider">|</span>
                <span class="class-students">{{ classItem.studentCount || 0 }}名学生</span>
              </div>
              <div class="other-subjects-info" v-if="classItem.otherSubjects && Object.keys(classItem.otherSubjects).length > 0">
                <div class="subjects-list">
                  <div 
                    v-for="(teacher, subject) in classItem.otherSubjects" 
                    :key="subject"
                    class="subject-info"
                    :class="{ 'unbound': !teacher }"
                  >
                    {{ subject }}：<span class="teacher-name">{{ teacher || '未绑定' }}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          <!-- 新增：班级操作提示 -->
          <div class="class-actions-prompt">
            <span class="footer-text">
              您也可以
              <el-button type="text" class="link-button" @click="goToCreateClass">新建班级</el-button>
              <span class="separator">或</span>
              <el-button type="text" class="link-button" @click="goToJoinClass">加入班级</el-button>
            </span>
          </div>
        </div>
      </div>
    </el-card>
  </div>
</template>

<script>
export default {
  name: 'ClassSelector',
  props: {
    deviceType: {
      type: String,
      default: 'pc',
      validator: (value) => ['mobile', 'pc'].includes(value)
    }
  },
  data() {
    return {
      loading: false,
      boundClasses: [],
      teacherInfo: null
    }
  },
  computed: {
    title() {
      return this.deviceType === 'mobile' ? '我的班级' : '选择要进入的班级';
    },
    buttonSize() {
      return this.deviceType === 'mobile' ? 'medium' : 'small';
    },
    groupedClasses() {
      // 直接使用后端返回的班级信息，包含完整的其他科目绑定情况
      return this.boundClasses.map(cls => ({
        ...cls,
        // 如果后端没有返回otherSubjects，则创建默认值
        otherSubjects: cls.otherSubjects || {
          '语文': '未绑定',
          '数学': '未绑定',
          '英语': '未绑定'
        }
      }));
    }
  },
  async created() {
    await this.initialize();
  },
  methods: {
    /**
     * 初始化组件
     */
    async initialize() {
      this.loading = true;
      try {
        // 获取教师信息
        this.teacherInfo = this.getTeacherInfo();
        if (!this.teacherInfo) {
          this.$message.error('请先登录');
          this.redirectToLogin();
          return;
        }
        
        // 获取绑定的班级列表
        await this.fetchBoundClasses();
        
      } catch (error) {
        console.error('初始化失败:', error);
        this.$message.error('加载失败，请刷新页面重试');
      } finally {
        this.loading = false;
      }
    },
    
    /**
     * 获取教师信息
     */
    getTeacherInfo() {
      try {
        const teacherInfoStr = localStorage.getItem('teacherInfo');
        return teacherInfoStr ? JSON.parse(teacherInfoStr) : null;
      } catch (error) {
        console.error('解析教师信息失败:', error);
        return null;
      }
    },
    
    /**
     * 获取绑定的班级列表
     */
    async fetchBoundClasses() {
      try {
        console.log('开始获取教师绑定的班级列表');
        // 调用新的接口，不再需要传入教师ID，从JWT token中自动获取
        const response = await this.$http.get('/teachers/bound-classes');
        console.log('获取班级列表API响应:', response.data);
        
        if (response.data.success) {
          this.boundClasses = response.data.boundClasses || [];
          console.log('处理后的班级列表:', this.boundClasses);
          
          // 为每个班级添加学生数量字段（如果需要的话）
          this.boundClasses.forEach(classItem => {
            classItem.studentCount = classItem.studentCount || 0;
            console.log(`班级 ${classItem.className}: classId=${classItem.classId}, id=${classItem.id}`);
          });
        } else {
          throw new Error(response.data.message || '获取班级列表失败');
        }
      } catch (error) {
        console.error('获取班级列表失败:', error);
        // 如果是认证失败，跳转到登录页面
        if (error.response && error.response.status === 401) {
          this.$message.error('登录已过期，请重新登录');
          this.redirectToLogin();
        } else {
          throw error;
        }
      }
    },
    
    /**
     * 选择班级并进入
     */
    selectClass(classItem) {
      console.log('=== 开始处理班级选择 ===');
      console.log('传入的班级对象:', classItem);
      console.log('classItem.classId:', classItem.classId);
      console.log('classItem.id:', classItem.id);
      
      // 使用classId而不是id字段
      const classId = classItem.classId || classItem.id;
      console.log('最终使用的classId:', classId);
      
      if (!classId) {
        console.error('班级ID为空，无法进入班级');
        this.$message.error('班级信息不完整，无法进入');
        return;
      }
      
      // 构造跳转路径
      const targetPath = this.deviceType === 'mobile' 
        ? `/mobile/class/${classId}`
        : `/class/${classId}`;
      
      console.log('构造的目标路径:', targetPath);
        
      // 添加科目参数
      const query = { subjectId: classItem.subjectId };
      console.log('查询参数:', query);
      
      console.log('准备跳转到:', { path: targetPath, query: query });
      this.$router.push({
        path: targetPath,
        query: query
      });
      console.log('=== 班级选择处理完成 ===');
    },
    
    /**
     * 跳转到加入班级页面
     */
    goToJoinClass() {
      const path = this.deviceType === 'mobile' ? '/joinclassmobile' : '/joinclass';
      this.$router.push(path);
    },
    
    /**
     * 跳转到创建班级页面
     */
    goToCreateClass() {
      const path = this.deviceType === 'mobile' ? '/newclassmobile' : '/newclass';
      this.$router.push(path);
    },
    
    /**
     * 跳转到登录页面
     */
    redirectToLogin() {
      const path = this.deviceType === 'mobile' ? '/loginmobile' : '/login';
      this.$router.push(path);
    },
    
    /**
     * 获取科目名称
     */
    getSubjectName(subjectId) {
      const subjects = {
        1: '语文',
        2: '数学',
        3: '英语'
      };
      return subjects[subjectId] || '未知科目';
    },
    
    /**
     * 获取班级项目样式类
     */
    getClassItemClass(classItem) {
      const classes = ['class-item'];
      if (this.deviceType === 'mobile') {
        classes.push('mobile-item');
      }
      return classes;
    },
    

  }
}
</script>

<style scoped>
.class-selector {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 100vh;
  padding: 20px;
  background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
}

.selector-card {
  width: 100%;
  max-width: 600px;
  border-radius: 15px;
  overflow: hidden;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
}

.selector-header {
  text-align: center;
  background: linear-gradient(90deg, #409EFF, #64b5f6);
  color: white;
  padding: 20px;
  margin: -20px -20px 20px -20px;
}

.selector-header h2 {
  margin: 0 0 10px 0;
  font-size: 24px;
  font-weight: bold;
}

.welcome-message {
  font-size: 14px;
  opacity: 0.9;
}

.empty-state {
  text-align: center;
  padding: 40px 20px;
}

.empty-icon {
  font-size: 48px;
  color: #c0c4cc;
  margin-bottom: 15px;
}

.empty-state p {
  font-size: 16px;
  color: #909399;
  margin-bottom: 25px;
}

.action-buttons {
  display: flex;
  gap: 15px;
  justify-content: center;
}

.class-list {
  display: flex;
  flex-direction: column;
  gap: 15px;
}

/* 班级操作提示样式 */
.class-actions-prompt {
  text-align: center;
  padding: 20px;
  background: rgba(255, 255, 255, 0.8);
  border-radius: 10px;
  margin-top: 20px;
  border: 1px solid #ebeef5;
}

.footer-text {
  color: #606266;
  font-size: 14px;
}

.link-button {
  color: #409EFF;
  padding: 0;
  font-size: 14px;
  vertical-align: baseline;
  margin: 0;
}

.separator {
  margin: 0 2px;
  color: #606266;
}

.link-button:hover {
  color: #409EFF;
}

.class-item {
  padding: 15px 20px;
  border: 1px solid #ebeef5;
  border-radius: 10px;
  background: white;
  transition: all 0.3s ease;
  cursor: pointer;
}

.class-item:hover {
  border-color: #409EFF;
  box-shadow: 0 4px 12px rgba(64, 158, 255, 0.15);
  transform: translateY(-2px);
}

.class-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
}

.class-header .class-name {
  font-size: 18px;
  font-weight: bold;
  color: #303133;
}

.class-header .class-actions {
  display: flex;
  align-items: center;
}

.class-item.mobile-item {
  padding: 20px;
}

.class-item.mobile-item .class-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
}

.class-item.mobile-item .class-actions {
  display: flex;
  align-items: center;
}

.class-main-info {
  margin-top: 8px;
}

.class-name {
  font-size: 18px;
  font-weight: bold;
  color: #303133;
  margin-bottom: 8px;
}

.class-details {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  color: #909399;
}

.class-current-subject {
  color: #409EFF;
  font-weight: 500;
}

.class-divider {
  color: #c0c4cc;
}

.class-students {
  color: #606266;
}

.other-subjects-info {
  margin-top: 8px;
  padding: 8px 12px;
  background-color: #f8f9fa;
  border-radius: 6px;
  border-left: 3px solid #409EFF;
}

.other-subjects-title {
  font-size: 12px;
  color: #909399;
  margin-bottom: 6px;
  font-weight: 500;
}

.subjects-list {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.subject-info {
  font-size: 13px;
  color: #606266;
}

.subject-info .teacher-name {
  color: #409EFF;
  font-weight: 500;
}

.subject-info.unbound .teacher-name {
  color: #f56c6c;
  font-style: italic;
}

.class-actions {
  display: flex;
  align-items: center;
}

/* 移动端适配 */
@media screen and (max-width: 768px) {
  .class-selector {
    padding: 10px;
  }
  
  .selector-card {
    max-width: 100%;
  }
  
  .selector-header h2 {
    font-size: 20px;
  }
  
  .action-buttons {
    flex-direction: column;
    gap: 10px;
    align-items: center;
  }
  
  .action-buttons .el-button {
    width: 100%;
    margin-left: 0 !important;
    margin-right: 0 !important;
  }
  
  .subjects-list {
    gap: 4px;
  }
  
  .other-subjects-info {
    padding: 6px 10px;
  }
}
</style>